import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

// @mui material components
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// API functions

import DocumentModal from './DocumentModal';

/**
 * Document action button for client row.
 * @param {Object} props
 * @param {string} props.clientId
 * @param {string} props.clientName
 * @param {string} props.clientType
 */
function DocumentButton({ clientId, clientName, clientType, templateFolders = [] }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  // Use templateFolders prop instead of fetching
  useEffect(() => {
    if (templateFolders && templateFolders.length > 0) {
      setFolders(templateFolders);
      setLoading(false);
      setError(null);
      // Initialize all folders as collapsed
      setExpandedFolders(new Set());
    }
  }, [templateFolders]);

  const handleClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleDocumentSelect = useCallback((template) => {
    console.log(`Creating document '${template.name}' (id: ${template.id}) for client ${clientId}`);
    setSelectedTemplate(template);
    setDocumentModalOpen(true);
    handleClose();
  }, [clientId, handleClose]);

  const handleDocumentModalClose = useCallback(() => {
    setDocumentModalOpen(false);
    setSelectedTemplate(null);
  }, []);

  const toggleFolder = useCallback((folderId) => {
    setExpandedFolders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  }, []);

  const isFolderExpanded = useCallback((folderId) => {
    return expandedFolders.has(folderId);
  }, [expandedFolders]);

  return (
    <>
      <MDButton
        variant="gradient"
        color="success"
        size="small"
        onClick={handleClick}
        startIcon={<Icon sx={{ fontSize: '1rem' }}>add_circle</Icon>}
        endIcon={<Icon sx={{ fontSize: '1rem' }}>expand_more</Icon>}
        sx={{
          borderRadius: '20px',
          textTransform: 'none',
          fontWeight: '600',
          fontSize: '0.75rem',
          px: 2,
          py: 0.5,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          '&:hover': {
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease-in-out',
        }}
      >
        New Document
      </MDButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            maxHeight: 450,
            width: 380,
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        {/* Search bar */}
        <MenuItem disableRipple sx={{ py: 1, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}>
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 6,
              border: '1px solid #ddd',
              fontSize: '0.95rem',
              outline: 'none',
            }}
            autoFocus
          />
        </MenuItem>
        {loading && (
          <MenuItem disabled>
            <ListItemText>
              <MDTypography variant="body2" color="text.secondary">
                Loading templates...
              </MDTypography>
            </ListItemText>
          </MenuItem>
        )}
        {error && (
          <MenuItem disabled>
            <ListItemText>
              <MDTypography variant="body2" color="error">
                {error}
              </MDTypography>
            </ListItemText>
          </MenuItem>
        )}
        {!loading && !error && folders.length === 0 && (
          <MenuItem disabled>
            <ListItemText>
              <MDTypography variant="body2" color="text.secondary">
                No folders found
              </MDTypography>
            </ListItemText>
          </MenuItem>
        )}
        {folders.map((folder, groupIndex) => {
          // For 'All Templates', flatten all templates from all folders
          let templates = folder.templates;
          if (folder.name === 'All Templates') {
            templates = folders.flatMap(f => f.templates || []);
          }
          // Filter templates by search
          const filteredTemplates = search.trim()
            ? (templates || []).filter(t => t.name.toLowerCase().includes(search.trim().toLowerCase()))
            : templates;
          const isExpanded = isFolderExpanded(folder.id);
          return (
            <div key={folder.id}>
              <MenuItem 
                onClick={() => toggleFolder(folder.id)}
                sx={{ 
                  backgroundColor: 'grey.100', 
                  borderBottom: '2px solid #eee', 
                  py: 1.5, 
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'grey.200' },
                  transition: 'background-color 0.2s ease'
                }}
              >
                <ListItemIcon>
                  <Icon sx={{ fontSize: '1.2rem', color: 'info.main' }}>
                    {isExpanded ? 'expand_less' : 'expand_more'}
                  </Icon>
                </ListItemIcon>
                <ListItemText>
                  <MDTypography variant="button" fontWeight="bold" color="info.dark" sx={{ fontSize: '0.875rem' }}>
                    {folder.name}
                  </MDTypography>
                </ListItemText>
              </MenuItem>
              {isExpanded && filteredTemplates && filteredTemplates.length > 0 ? (
                filteredTemplates.map((template) => (
                  <MenuItem key={template.id} onClick={() => handleDocumentSelect(template)} sx={{ pl: 4, py: 1.5, '&:hover': { backgroundColor: 'grey.100' }, transition: 'background-color 0.2s ease' }}>
                    <ListItemText>
                      <MDTypography variant="body2" color="text.primary" sx={{ fontSize: '0.8rem', fontWeight: '500' }}>
                        {template.name}
                      </MDTypography>
                    </ListItemText>
                  </MenuItem>
                ))
              ) : isExpanded && (
                <MenuItem disabled sx={{ pl: 4 }}>
                  <ListItemText>
                    <MDTypography variant="body2" color="text.secondary">
                      No templates in this folder
                    </MDTypography>
                  </ListItemText>
                </MenuItem>
              )}
              {groupIndex < folders.length - 1 && <Divider sx={{ my: 1, opacity: 0.3 }} />}
            </div>
          );
        })}
      </Menu>
      
      {/* Document Modal */}
      <DocumentModal
        open={documentModalOpen}
        onClose={handleDocumentModalClose}
        templateId={selectedTemplate?.id}
        templateName={selectedTemplate?.name}
        clientId={clientId}
        clientType={clientType}
        clientName={clientName}
      />
    </>
  );
}

DocumentButton.propTypes = {
  clientId: PropTypes.string.isRequired,
  clientName: PropTypes.string.isRequired,
  clientType: PropTypes.string.isRequired,
  templateFolders: PropTypes.array,
};

export default DocumentButton; 